<script setup>
// Config do nó Botões/Lista WhatsApp — cabeçalho, corpo, rodapé, opções (1-3 = botões, 4-10 = lista).
// Cada opção é editável inline (número + input + remover); "+ Adicionar opção" cria item novo.
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FlowField from './FlowField.vue';

const props = defineProps({ node: { type: Object, required: true } });
const { t } = useI18n();
const d = computed(() => props.node.data);
const items = computed(() => d.value.items || []);
const isList = computed(() => items.value.length > 3);
const maxLen = computed(() => (isList.value ? 24 : 20));
const canAdd = computed(() => items.value.length < 10);

// id único e estável (não colide após remoções, ao contrário do esquema antigo por índice).
const uid = () =>
  `opt_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const addItem = () => {
  if (!canAdd.value) return;
  if (!d.value.items) d.value.items = [];
  d.value.items.push({ id: uid(), title: '' });
};
const removeItem = i => d.value.items.splice(i, 1);

onMounted(() => {
  if (d.value.timeoutSeconds === undefined) d.value.timeoutSeconds = 300;
  if (!d.value.items) d.value.items = [];
});
</script>

<template>
  <div>
    <FlowField
      :model-value="d.header"
      :label="t('CHATBOT.nodeConfig.sendWAInteractive.headerLabel')"
      type="text"
      :placeholder="t('CHATBOT.nodeConfig.sendWAInteractive.headerPlaceholder')"
      :hint="t('CHATBOT.nodeConfig.sendWAInteractive.headerHint')"
      @update:model-value="d.header = $event"
    />
    <FlowField
      :model-value="d.bodyText"
      :label="t('CHATBOT.nodeConfig.sendWAInteractive.bodyLabel')"
      type="textarea"
      :rows="3"
      :placeholder="t('CHATBOT.nodeConfig.sendWAInteractive.bodyPlaceholder')"
      picker
      @update:model-value="d.bodyText = $event"
    />
    <FlowField
      :model-value="d.footer"
      :label="t('CHATBOT.nodeConfig.sendWAInteractive.footerLabel')"
      type="text"
      :placeholder="t('CHATBOT.nodeConfig.sendWAInteractive.footerPlaceholder')"
      @update:model-value="d.footer = $event"
    />

    <!-- Opções -->
    <div class="pt-3 mt-3 border-t border-n-weak">
      <div class="flex items-center justify-between mb-1.5">
        <label class="text-sm font-medium text-n-slate-12">{{
          t('CHATBOT.nodeConfig.sendWAInteractive.optionsLabel')
        }}</label>
        <span
          class="inline-flex items-center px-2 py-0.5 text-[0.625rem] font-medium rounded-full"
          :class="
            isList
              ? 'bg-n-violet-3 text-n-violet-11'
              : 'bg-n-teal-3 text-n-teal-11'
          "
        >
          {{
            isList
              ? t('CHATBOT.nodeConfig.sendWAInteractive.listInfo', {
                  count: items.length,
                })
              : t('CHATBOT.nodeConfig.sendWAInteractive.buttonsInfo', {
                  count: items.length,
                })
          }}
        </span>
      </div>
      <p class="mb-2 text-[0.6875rem] text-n-slate-10">
        {{ t('CHATBOT.nodeConfig.sendWAInteractive.typeHint') }}
      </p>

      <div
        v-if="!items.length"
        class="px-3 py-4 mb-2 text-xs text-center border border-dashed rounded-lg border-n-weak text-n-slate-10"
      >
        {{ t('CHATBOT.nodeConfig.sendWAInteractive.addOptionsHint') }}
      </div>

      <div
        v-for="(it, i) in items"
        :key="it.id"
        class="flex items-center gap-2 mb-1.5"
      >
        <span
          class="grid w-5 h-5 text-[0.625rem] font-semibold rounded-full shrink-0 place-items-center"
          :class="
            isList
              ? 'bg-n-violet-3 text-n-violet-11'
              : 'bg-n-teal-3 text-n-teal-11'
          "
          >{{ i + 1 }}</span
        >
        <input
          v-model="it.title"
          class="flex-1 w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          :maxlength="maxLen"
          :placeholder="
            t('CHATBOT.nodeConfig.sendWAInteractive.optionPlaceholder', {
              n: i + 1,
            })
          "
        />
        <span class="text-[0.625rem] text-n-slate-9 w-9 text-right shrink-0"
          >{{ (it.title || '').length }}/{{ maxLen }}</span
        >
        <button
          class="grid rounded-md w-7 h-7 shrink-0 place-items-center text-n-ruby-11 hover:bg-n-ruby-3"
          :title="t('CHATBOT.nodeConfig.media.remove')"
          @click="removeItem(i)"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="w-4 h-4"
          >
            <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <button
        class="flex items-center justify-center w-full gap-1.5 px-3 py-2 mt-1 text-sm font-medium rounded-lg border border-dashed border-n-brand/50 text-n-brand hover:bg-n-brand/5 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canAdd"
        @click="addItem"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          class="w-4 h-4"
        >
          <path stroke-linecap="round" d="M12 5v14M5 12h14" />
        </svg>
        {{ t('CHATBOT.nodeConfig.sendWAInteractive.addOption') }}
      </button>
      <p class="mt-1.5 text-[0.6875rem] text-n-slate-10">
        {{ t('CHATBOT.nodeConfig.sendWAInteractive.optionsFallbackHint') }}
      </p>
    </div>

    <template v-if="isList">
      <div class="pt-3 mt-3 border-t border-n-weak">
        <FlowField
          :model-value="d.sectionTitle"
          :label="t('CHATBOT.nodeConfig.sendWAInteractive.sectionTitleLabel')"
          type="text"
          :placeholder="
            t('CHATBOT.nodeConfig.sendWAInteractive.sectionTitlePlaceholder')
          "
          @update:model-value="d.sectionTitle = $event"
        />
        <FlowField
          :model-value="d.buttonText"
          :label="t('CHATBOT.nodeConfig.sendWAInteractive.buttonTextLabel')"
          type="text"
          :placeholder="
            t('CHATBOT.nodeConfig.sendWAInteractive.buttonTextPlaceholder')
          "
          :hint="t('CHATBOT.nodeConfig.sendWAInteractive.buttonTextHint')"
          @update:model-value="d.buttonText = $event"
        />
      </div>
    </template>

    <div class="pt-3 mt-3 border-t border-n-weak">
      <FlowField
        :model-value="d.timeoutSeconds"
        :label="t('CHATBOT.nodeConfig.sendWAInteractive.timeoutLabel')"
        type="number"
        :min="0"
        :max="86400"
        :hint="t('CHATBOT.nodeConfig.sendWAInteractive.timeoutHint')"
        @update:model-value="d.timeoutSeconds = $event"
      />
    </div>

    <!-- limites WhatsApp -->
    <div
      class="p-2.5 mt-3 text-[0.6875rem] rounded-lg bg-n-alpha-1 border border-n-weak text-n-slate-11"
    >
      <p class="mb-1 font-medium text-n-slate-12">
        {{ t('CHATBOT.nodeConfig.sendWAInteractive.limitsTitle') }}
      </p>
      <ul class="pl-3 space-y-0.5 list-disc marker:text-n-slate-9">
        <li>{{ t('CHATBOT.nodeConfig.sendWAInteractive.limit1') }}</li>
        <li>{{ t('CHATBOT.nodeConfig.sendWAInteractive.limit2') }}</li>
        <li>{{ t('CHATBOT.nodeConfig.sendWAInteractive.limit3') }}</li>
        <li>{{ t('CHATBOT.nodeConfig.sendWAInteractive.limit4') }}</li>
        <li>{{ t('CHATBOT.nodeConfig.sendWAInteractive.limit5') }}</li>
      </ul>
    </div>
  </div>
</template>
